import { Controller } from '@hotwired/stimulus';
import {
  applyCaseExperienceState,
  patchDocumentUserState,
  waitForVisible,
  normalizeNodeUid,
} from '../../utils/case_experience/case_experience_in_rails';
import { applyDerivedPassiveSubmitInactive } from '../../utils/case_experience/passive_submit_eligibility';

export default class extends Controller {
  static targets = ['gradedBanner', 'select'];

  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    inactive: Boolean,
    nodeState: Object,
  };

  connect() {
    this._categorySaveInFlight = 0;
    const submitButtonContainer = this.element?.nextElementSibling;
    if (submitButtonContainer?.classList?.contains('doc-button-slice')) {
      this.submitButtonContainer = submitButtonContainer;
    } else {
      this.submitButtonContainer = null;
    }

    waitForVisible(this.element, () => {
      this.initializeCategoryMatcher();
    });
  }

  disconnect() {
    if (this.submitButtonContainer && this.submitButton && this._handleSubmit) {
      this.submitButton.removeEventListener('click', this._handleSubmit);
    }
    this.submitButton = null;
    this._handleSubmit = null;
    this.submitButtonContainer = null;
  }

  initializeCategoryMatcher() {
    const graded = this.nodeStateValue?.graded?.value || this.inactiveValue;
    if (graded) {
      this.disableSelectTargets();
    } else {
      this.enableSubmitButton();
      this.attachSubmitListener();
    }
  }

  enableSubmitButton() {
    const submitButton =
      this.submitButton || this.submitButtonContainer?.querySelector('button');
    if (submitButton) {
      this.submitButton = submitButton;
    }
    applyDerivedPassiveSubmitInactive({
      submitSlice: this.submitButtonContainer,
      application: this.application,
      buttonActionIdentifier: 'case-experience--button-action',
      enabled: true,
      submitButton,
    });
  }

  disableSubmitButton() {
    const submitButton =
      this.submitButton || this.submitButtonContainer?.querySelector('button');
    applyDerivedPassiveSubmitInactive({
      submitSlice: this.submitButtonContainer,
      application: this.application,
      buttonActionIdentifier: 'case-experience--button-action',
      enabled: false,
      submitButton,
    });
  }

  attachSubmitListener() {
    if (this.submitButtonContainer && this.submitButton) {
      if (this._handleSubmit) {
        this.submitButton.removeEventListener('click', this._handleSubmit);
      }
      this._handleSubmit = this.handleSubmit.bind(this);
      this.submitButton.addEventListener('click', this._handleSubmit);
    }
  }

  buildSubmitState(updatedAt) {
    const nodeUid = normalizeNodeUid(this.nodeUidValue);
    const submitUid = this.submitButtonContainer?.dataset?.uid;
    const state = {
      [nodeUid]: {
        graded: { value: true, updatedAt },
        inactive: { value: 2, updatedAt },
      },
    };
    if (submitUid) {
      state[submitUid] = {
        inactive: { value: 2, updatedAt },
      };
    }
    return state;
  }

  async handleSubmit() {
    try {
      const updatedAt = Date.now();
      const state = this.buildSubmitState(updatedAt);
      const response = await applyCaseExperienceState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        nodeUid: this.nodeUidValue,
        body: { document_user_state: { state } },
      });
      const nodeUid = normalizeNodeUid(this.nodeUidValue);
      this.nodeStateValue = response?.state?.[nodeUid] || {
        ...(this.nodeStateValue || {}),
        graded: { value: true, updatedAt },
        inactive: { value: 2, updatedAt },
      };
      this.refreshAfterSubmit(response || { state });
      this.disableSubmitButton();
    } catch (error) {
      console.error('Error in handleSubmit:', error);
    }
  }

  refreshAfterSubmit(response) {
    this.initializeCategoryMatcher();
    this.applySubmitButtonInactiveState(response);
  }

  applySubmitButtonInactiveState(response) {
    const buttonUid = this.submitButtonContainer?.dataset?.uid;
    const inactive = response?.state?.[buttonUid]?.inactive?.value;
    if (inactive === undefined || inactive === null) return;

    this.submitButtonContainer.setAttribute(
      'data-case-experience--button-action-inactive-value',
      String(inactive),
    );

    const buttonAction = this.application?.getControllerForElementAndIdentifier(
      this.submitButtonContainer,
      'case-experience--button-action',
    );

    if (buttonAction) {
      buttonAction.inactiveValue = Number(inactive);
      return;
    }

    const submitButton = this.submitButton || this.submitButtonContainer.querySelector('button');
    if (!submitButton) return;

    const disabled = Number(inactive) === 2;
    submitButton.disabled = disabled;
    submitButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  disableSelectTargets() {
    this.inactiveValue = true;
    this.selectTargets.forEach((select) => {
      select.disabled = true;
      select.setAttribute('aria-disabled', 'true');
      this.disableDropdownForSelect(select);
    });
  }

  disableDropdownForSelect(select) {
    const shell = select.closest('.dropdown-shell');
    if (!shell) return;

    const trigger = shell.querySelector('button.dropdown-field-control');
    if (trigger) {
      trigger.disabled = true;
      trigger.classList.add('dropdown-field-control-disabled');
    }

    shell
      .querySelectorAll(
        '[data-railsui-dropdown-target="menu"], [data-railsui-dropdown-menu-target="menu"]',
      )
      .forEach((menu) => menu.classList.add('hidden'));
  }

  async selectCategory(event) {
    if (this.inactiveValue) return;

    const select = event.target;
    const itemUid = select.dataset.itemUid;
    const categoryUid = select.value;

    this.disableSubmitButton();
    this._categorySaveInFlight += 1;

    try {
      const currentValue = await this.getCurrentValue();

      const newValue = {};
      Object.keys(currentValue).forEach((catUid) => {
        newValue[catUid] = currentValue[catUid].filter((iUid) => iUid !== itemUid);
      });

      if (categoryUid !== 'default') {
        if (!newValue[categoryUid]) {
          newValue[categoryUid] = [];
        }
        newValue[categoryUid].push(itemUid);
      }

      const body = {
        document_user_state: {
          state: {
            [this.nodeUidValue]: {
              value: {
                value: newValue,
                updatedAt: new Date().getTime(),
              },
            },
          },
        },
      };

      await patchDocumentUserState({
        documentId: this.documentIdValue,
        userId: this.userIdValue,
        body,
      });
    } catch (error) {
      console.error('Error in selectCategory:', error);
    } finally {
      this._categorySaveInFlight = Math.max(0, this._categorySaveInFlight - 1);
      if (this._categorySaveInFlight === 0 && !this.inactiveValue) {
        this.enableSubmitButton();
      }
    }
  }

  getCurrentValue() {
    const selects = this.selectTargets;
    const value = {};
    selects.forEach((select) => {
      const itemUid = select?.dataset?.itemUid;
      const categoryUid = select?.value;
      if (categoryUid && categoryUid !== 'default') {
        if (!value[categoryUid]) {
          value[categoryUid] = [];
        }
        value[categoryUid].push(itemUid);
      }
    });
    return value;
  }
}
